"use client";

import { useEffect } from "react";
import Link from "next/link";
import type { Route } from "next";
import { Compass, RotateCcw, TriangleAlert } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/layout/PageContainer";

/**
 * Shared bodies for the App Router boundary files (backlog R15).
 *
 * Until now the tree had **no** `error.tsx`, `loading.tsx` or `not-found.tsx`
 * anywhere: a throw inside any client page escaped to Next's own full-screen
 * error page, which discards the entire shell — sidebar, header, session
 * context, everything — for what is usually one broken panel. With a boundary at
 * the `(app)` group (and one per busy area) the failure is contained to `<main>`
 * and the user can navigate away instead of reaching for the back button.
 *
 * Kept in ONE component because Next requires a default-exported file per
 * segment: the per-segment files stay three lines each and cannot drift.
 */

export interface RouteErrorProps {
  error: Error & { digest?: string };
  /**
   * Next 16 renamed the recovery callback to `unstable_retry` (it re-fetches
   * AND re-renders); `reset` remains for the render-only case. Both are
   * optional here so the same body serves every segment and any future rename
   * degrades to "reload the route" rather than crashing the boundary itself.
   */
  unstable_retry?: () => void;
  reset?: () => void;
}

/** The in-shell error body: contained, explained, recoverable. */
export function RouteError({ error, unstable_retry, reset }: RouteErrorProps) {
  const { t } = useTranslation();

  useEffect(() => {
    // The BFF route handlers are the only place with a real logger; from the
    // client the console is what a support engineer will actually be asked for.
    console.error("[route-error]", error);
  }, [error]);

  const retry = unstable_retry ?? reset;

  return (
    // Centered on BOTH axes, not just horizontally. This body is also the
    // pre-auth boundary (`app/error.tsx` serves `/login`, `/select-instance`,
    // `/set-password`), and there it renders as a direct flex child of `<body>`
    // with no shell around it — so the plain `mx-auto` card sat flush against
    // the top-left corner of an otherwise empty viewport. `flex-1` + centering
    // works for both hosts: `<body>` is `min-h-dvh flex flex-col` and `<main>`
    // is `flex min-h-0 flex-1 flex-col`, so the boundary fills whatever space
    // its host has and parks the card in the middle of it.
    <PageContainer
      variant="form"
      fullHeight
      className="flex-1 items-center justify-center py-6"
    >
      <div
        role="alert"
        data-testid="route-error"
        className="w-full max-w-lg rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center"
      >
        <TriangleAlert className="mx-auto size-8 text-destructive" aria-hidden />
        <h2 className="mt-3 text-lg font-semibold">{t("shell.boundary.errorTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("shell.boundary.errorDescription")}
        </p>
        {error.digest ? (
          <p className="mt-2 font-mono text-xs text-muted-foreground">
            {t("shell.boundary.reference")}: {error.digest}
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {retry ? (
            <Button onClick={retry} data-testid="route-error-retry">
              <RotateCcw className="size-4" aria-hidden />
              {t("shell.boundary.retry")}
            </Button>
          ) : null}
          <Button asChild variant="outline">
            <Link href={"/" as Route}>{t("shell.boundary.toStart")}</Link>
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}

/**
 * The streaming fallback. Skeletons sit at the geometry of a typical page
 * (title, toolbar, table) so the real content swaps in without a layout jump —
 * the same rule the sidebar and `IsmsGuard` skeletons follow (R9).
 */
export function RouteLoading() {
  const { t } = useTranslation();
  return (
    <PageContainer aria-busy="true" data-testid="route-loading">
      <span className="sr-only">{t("shell.boundary.loading")}</span>
      <Skeleton className="h-7 w-72" />
      <div className="flex gap-2">
        <Skeleton className="h-9 w-44" />
        <Skeleton className="h-9 w-28" />
      </div>
      <Skeleton className="h-72 w-full" />
    </PageContainer>
  );
}

/** The in-shell 404 body — a dead end with two ways out (`frontend.md`). */
export function RouteNotFound() {
  const { t } = useTranslation();
  return (
    // Same centering as {@link RouteError}, and for the same reason — a dead end
    // pinned to the top-left corner reads as a broken layout on top of a 404.
    <PageContainer
      variant="form"
      fullHeight
      className="flex-1 items-center justify-center py-6"
    >
      <div
        data-testid="route-not-found"
        className="w-full max-w-lg rounded-lg border border-border bg-card p-6 text-center"
      >
        <Compass className="mx-auto size-8 text-muted-foreground" aria-hidden />
        <h2 className="mt-3 text-lg font-semibold">
          {t("shell.boundary.notFoundTitle")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("shell.boundary.notFoundDescription")}
        </p>
        <div className="mt-4">
          <Button asChild>
            <Link href={"/" as Route}>{t("shell.boundary.toStart")}</Link>
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
